(() => {
  'use strict';

  const PATCH_FLAG = '__dpDisplaySizingPatched';
  const CM_PER_INCH = 2.54;
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const tidy = (value, digits = 1) => String(Number(Number(value).toFixed(digits)));

  function getDisplayMap(instance) {
    if (Object.prototype.hasOwnProperty.call(instance, '__dpDisplaySizeMap')) {
      return instance.__dpDisplaySizeMap;
    }

    const handle = String(instance?.rawConfig?.productHandle || instance?.config?.productHandle || '').trim();
    const nodes = Array.from(document.querySelectorAll('script[data-dp-size-display-map]'));
    let node = handle ? nodes.find((item) => item.dataset.productHandle === handle) : null;

    if (!node && nodes.length === 1) node = nodes[0];

    let map = null;
    if (node) {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) map = parsed;
      } catch (_error) {
        map = null;
      }
    }

    instance.__dpDisplaySizeMap = map;
    return map;
  }

  function mappedValue(map, key) {
    if (!map || key === undefined || key === null || key === '') return null;

    const direct = map[key];
    if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
      return String(direct).trim();
    }

    const normalizedKey = normalize(key);
    const matchingKey = Object.keys(map).find((candidate) => normalize(candidate) === normalizedKey);
    if (!matchingKey) return null;

    const value = map[matchingKey];
    return value !== undefined && value !== null && String(value).trim() !== ''
      ? String(value).trim()
      : null;
  }

  function displayLabel(instance, sizeOrLabel) {
    const isSizeObject = sizeOrLabel && typeof sizeOrLabel === 'object';
    const fallback = isSizeObject ? String(sizeOrLabel.label ?? '') : String(sizeOrLabel ?? '');
    const map = getDisplayMap(instance);

    if (!map) return fallback;

    const candidates = [];
    if (isSizeObject) {
      try {
        const optionValue = typeof instance.findOptionValue === 'function'
          ? instance.findOptionValue(sizeOrLabel)
          : null;
        candidates.push(optionValue?.label);
      } catch (_error) {
        // Keep display-only sizing non-blocking if the base picker changes.
      }

      if (Number.isInteger(sizeOrLabel.index)) {
        candidates.push(instance?.config?.optionValues?.[sizeOrLabel.index]?.label);
      }

      candidates.push(sizeOrLabel.optionValue?.label, sizeOrLabel.supplierLabel, sizeOrLabel.label);
    } else {
      candidates.push(sizeOrLabel);
    }

    for (const candidate of candidates) {
      const mapped = mappedValue(map, candidate);
      if (mapped) return mapped;
    }

    return fallback;
  }

  function formatLength(instance, valueCm) {
    const cm = Math.max(0, Number(valueCm) || 0);
    const unit = normalize(instance?.unit);
    const imperial = unit.includes('imperial') || ['in', 'inch', 'inches'].includes(unit);

    return imperial ? `${tidy(cm / CM_PER_INCH)} in` : `${tidy(cm)} cm`;
  }

  function replaceCanonicalLabel(text, canonical, display) {
    const source = String(text ?? '');
    const raw = String(canonical ?? '').trim();
    const shown = String(display ?? '').trim();

    if (!raw || !shown || raw === shown) return source;

    const escaped = raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return source.replace(new RegExp(`\\b${escaped}\\b`, 'g'), shown);
  }

  function noMatchCopy(instance, result) {
    const candidate = result?.closest;
    if (!candidate?.size) {
      return 'Compare your measurements with the garment size chart for the closest fit.';
    }

    const label = displayLabel(instance, candidate.size);
    const calculations = candidate.calculations || {};
    const body = result.body || {};
    const sources = body.sources || {};
    const profileType = normalize(instance?.config?.profileType);

    const waistRoom = Number(calculations.waistRoom);
    const hipDeficit = Number(calculations.hipDeficit);
    const chestDeficit = Number(calculations.chestDeficit);

    if (profileType === 'bottom' && sources.waist === 'exact' && Number.isFinite(waistRoom)) {
      if (waistRoom < 0) {
        return `Size ${label} is closest, but its garment waist is about ${formatLength(instance, Math.abs(waistRoom))} smaller than your waist, so it will likely feel too tight.`;
      }

      if (waistRoom <= 3) {
        return `Size ${label} is closest, but it leaves only about ${formatLength(instance, waistRoom)} of room at the waist, so it may feel snug.`;
      }
    }

    if (profileType === 'bottom' && sources.hip === 'exact' && Number.isFinite(hipDeficit) && hipDeficit > 0) {
      return `Size ${label} is closest, but it may fit tighter through the hips than intended.`;
    }

    if (profileType === 'top' && sources.chest === 'exact' && Number.isFinite(chestDeficit) && chestDeficit > 0) {
      return `Size ${label} is closest, but it may fit tighter through the chest than intended.`;
    }

    if (profileType === 'bottom' && (sources.waist !== 'exact' || sources.hip !== 'exact')) {
      return `Size ${label} is closest, but the fit is borderline based on your profile. Exact waist and hip measurements will give a firmer match.`;
    }

    if (profileType === 'top' && sources.chest !== 'exact') {
      return `Size ${label} is closest, but the fit is borderline based on your profile. An exact chest measurement will give a firmer match.`;
    }

    return `Size ${label} is the closest option, but the fit is borderline. Check the garment measurements before ordering.`;
  }

  function applyResultDisplay(instance, result) {
    if (!result) return;

    if (result.matched && result.recommended?.size) {
      const size = result.recommended.size;
      const canonical = String(size.label ?? '');
      const shown = displayLabel(instance, size);
      const heading = instance.querySelector('[data-size-result-label]');
      const selectButton = instance.querySelector('[data-select-size]');

      if (heading && shown) heading.textContent = shown;
      if (selectButton) {
        selectButton.textContent = replaceCanonicalLabel(selectButton.textContent, canonical, shown);
      }

      if (shown && typeof instance.updateTriggerRecommendation === 'function') {
        instance.updateTriggerRecommendation(shown);
      }
      return;
    }

    const noMatch = instance.querySelector('[data-size-no-match]');
    if (!noMatch) return;

    const eyebrow = noMatch.querySelector('.size-recommender__eyebrow');
    const heading = noMatch.querySelector('h2');
    const copy = noMatch.querySelector('[data-size-no-match-copy]');
    const closestSize = result.closest?.size;

    if (closestSize) {
      const shown = displayLabel(instance, closestSize);
      if (eyebrow) eyebrow.textContent = 'Closest available size';
      if (heading) heading.textContent = `Closest size: ${shown}`;
      if (copy) copy.textContent = noMatchCopy(instance, result);
    } else {
      if (eyebrow) eyebrow.textContent = 'Fit guidance';
      if (heading) heading.textContent = 'Check the size chart';
      if (copy) copy.textContent = 'Compare your measurements with the garment size chart for the closest fit.';
    }
  }

  function patchRecommender() {
    const Recommender = customElements.get('size-recommender');
    if (!Recommender || Recommender[PATCH_FLAG]) return;

    Recommender[PATCH_FLAG] = true;
    const proto = Recommender.prototype;

    proto.dpDisplaySizeLabel = function dpDisplaySizeLabel(sizeOrLabel) {
      return displayLabel(this, sizeOrLabel);
    };

    const originalUpdateTrigger = proto.updateTriggerRecommendation;
    if (typeof originalUpdateTrigger === 'function') {
      proto.updateTriggerRecommendation = function updateTriggerRecommendation(label) {
        const shown = label ? displayLabel(this, label) : label;
        return originalUpdateTrigger.call(this, shown);
      };
    }

    const originalResultCopy = proto.resultCopy;
    if (typeof originalResultCopy === 'function') {
      proto.resultCopy = function resultCopy(result) {
        const copy = originalResultCopy.call(this, result);
        const size = result?.recommended?.size;
        if (!size) return copy;

        return replaceCanonicalLabel(copy, size.label, displayLabel(this, size));
      };
    }

    const originalRenderResult = proto.renderResult;
    if (typeof originalRenderResult === 'function') {
      proto.renderResult = function renderResult(result, options) {
        const value = originalRenderResult.call(this, result, options);
        applyResultDisplay(this, result);
        return value;
      };
    }

    const originalSelectRecommendedSize = proto.selectRecommendedSize;
    if (typeof originalSelectRecommendedSize === 'function') {
      proto.selectRecommendedSize = function selectRecommendedSize(...args) {
        const size = this.lastResult?.recommended?.size;
        const canonical = size?.label;
        const shown = size ? displayLabel(this, size) : null;
        const value = originalSelectRecommendedSize.apply(this, args);
        const button = this.querySelector('[data-select-size]');

        if (button && canonical && shown) {
          button.textContent = replaceCanonicalLabel(button.textContent, canonical, shown);
        }
        return value;
      };
    }

    document.querySelectorAll('size-recommender').forEach((instance) => {
      if (instance.lastResult && typeof instance.renderResult === 'function') {
        instance.renderResult(instance.lastResult, { moveFocus: false });
      }
    });
  }

  if (customElements.get('size-recommender')) {
    patchRecommender();
  } else {
    customElements.whenDefined('size-recommender').then(patchRecommender);
  }
})();